"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useLocale } from "next-intl";
import {
  Home,
  ChevronRight,
  Plane,
  DoorOpen,
  RotateCcw,
  Loader2,
} from "lucide-react";
import type { BoardingPassData } from "bcbp";
import { fetchVuelosFinal } from "@/lib/api/vuelos/fetchVuelos";
import type { Vuelos } from "@/lib/api/vuelos/types";
import BoardingScanner from "./BoardingScanner";
import { greetingName } from "./passengerName";
import GateMap from "./GateMap";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/duty4.jpg";

type Step = "scan" | "searching" | "result" | "error";

export default function Scaner() {
  const locale = useLocale();

  const [step, setStep] = useState<Step>("scan");
  const [error, setError] = useState<string>("");
  const [flightCode, setFlightCode] = useState<string>("");
  const [vuelo, setVuelo] = useState<Vuelos | null>(null);
  const [passenger, setPassenger] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Tras escanear, lleva al usuario al área de resultado (estaba haciendo scroll
  // manual desde el footer hasta encontrar la búsqueda/el vuelo).
  useEffect(() => {
    if (step === "searching" || step === "result" || step === "error") {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  // Busca el vuelo combinando aerolínea + número del boarding pass.
  async function findFlight(
    airline: string,
    num: string,
  ): Promise<Vuelos | null> {
    const numNoZeros = num.replace(/^0+/, "");

    // 1) Intento directo con el filtro `flight` (varios formatos posibles).
    const candidates = [`${airline}${numNoZeros}`, `${airline}${num}`];
    for (const flight of candidates) {
      const res = await fetchVuelosFinal({ flight, airport: "pty" });
      if (res.length > 0) return res[0];
    }

    // 2) Fallback: listar salidas de hoy y filtrar por aerolínea + número.
    const deps = await fetchVuelosFinal({
      airport: "pty",
      type: "departure",
      day: "hoy",
    });
    const match = deps.find(
      (v) =>
        v.vuelo.toUpperCase() === airline.toUpperCase() &&
        v.vueloNum.replace(/^0+/, "") === numNoZeros,
    );
    return match ?? null;
  }

  async function handleScanned(data: BoardingPassData) {
    setPassenger(greetingName(data.passengerName));

    const leg = data.legs?.[0];
    const airline = leg?.operatingCarrierDesignator?.trim();
    const num = leg?.flightNumber?.trim();

    if (!airline || !num) {
      setError("El boarding pass no trae aerolínea o número de vuelo legibles.");
      setStep("error");
      return;
    }

    const code = `${airline}${num.replace(/^0+/, "")}`;
    setFlightCode(code);
    setStep("searching");
    setError("");

    try {
      const found = await findFlight(airline, num);
      if (!found) {
        setError(`No se encontró el vuelo ${code} en el sistema.`);
        setStep("error");
        return;
      }
      setVuelo(found);
      setStep("result");
    } catch (e) {
      const err = e as Error;
      setError(`Error consultando el vuelo: ${err.message}`);
      setStep("error");
    }
  }

  function restart() {
    setStep("scan");
    setError("");
    setVuelo(null);
    setFlightCode("");
    setPassenger(null);
  }

  const eyebrow = (label: string) => (
    <span
      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
      style={{ color: NAVY }}
    >
      <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
      {label}
    </span>
  );

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[360px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center blur-xs"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-14">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>Inicio</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li aria-current="page" className="text-white font-medium">
                Ruta a tu puerta
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              Boarding pass
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              Encuentra la ruta a tu puerta de salida
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              Escanea tu boarding pass y te mostramos cómo llegar a la puerta.
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white">
        <div
          ref={contentRef}
          className="max-w-[80%] mx-auto py-16 md:py-24 scroll-mt-4"
        >
          {step === "scan" && (
            <div className="max-w-2xl">
              {eyebrow("Paso 1 — Escanear")}
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight mb-3">
                Escanea tu boarding pass
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Pulsa el botón para abrir la cámara y apuntar al código de
                barras de tu boarding pass.
              </p>
              <BoardingScanner onScanned={handleScanned} />
            </div>
          )}

          {step === "searching" && (
            <div className="max-w-2xl flex items-center gap-3 text-lg text-gray-700">
              <Loader2
                className="w-6 h-6 animate-spin"
                style={{ color: NAVY }}
              />
              {passenger ? `Un momento, ${passenger}: ` : "Un momento: "}
              buscamos tu vuelo <strong>{flightCode}</strong>…
            </div>
          )}

          {step === "error" && (
            <div className="max-w-2xl">
              {eyebrow("Algo salió mal")}
              <div
                className="p-5 rounded-xl mb-6"
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#991b1b",
                  borderLeft: "3px solid #dc2626",
                }}
              >
                {error}
              </div>
              <button
                onClick={restart}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wider font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                <RotateCcw className="w-4 h-4" />
                Escanear de nuevo
              </button>
            </div>
          )}

          {step === "result" && vuelo && (
            <div>
              <div className="max-w-2xl mb-12">
                <h2
                  className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-balance"
                  style={{ color: NAVY }}
                >
                  {passenger ? `Hola, ${passenger}` : "¡Hola!"}
                </h2>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                  {vuelo.puerta?.trim()
                    ? "Aquí te mostramos la ruta para llegar a tu puerta. Te acompañamos hasta allá."
                    : "Ya tenemos tu vuelo. En cuanto asignen la puerta, te mostramos la ruta para llegar."}
                </p>
              </div>

              {eyebrow("Paso 2 — Tu vuelo")}

              {/* Resumen del vuelo */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 mb-10">
                <div className="bg-white p-6 flex flex-col gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Plane className="w-3.5 h-3.5" /> Vuelo
                  </span>
                  <span
                    className="text-2xl font-bold tracking-tight"
                    style={{ color: NAVY }}
                  >
                    {vuelo.vuelo}
                    {vuelo.vueloNum}
                  </span>
                  <span className="text-sm text-gray-600">
                    {vuelo.aerolinea}
                  </span>
                </div>

                <div className="bg-white p-6 flex flex-col gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                    Destino
                  </span>
                  <span
                    className="text-2xl font-bold tracking-tight"
                    style={{ color: NAVY }}
                  >
                    {vuelo.routing || "—"}
                  </span>
                  <span className="text-sm text-gray-600">
                    {vuelo.routingDescEs}
                    {vuelo.estado ? ` · ${vuelo.estado}` : ""}
                  </span>
                </div>

                <div
                  className="p-6 flex flex-col gap-2"
                  style={{ backgroundColor: NAVY }}
                >
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/60 flex items-center gap-2">
                    <DoorOpen className="w-3.5 h-3.5" /> Puerta
                  </span>
                  <span
                    className="text-4xl font-bold tracking-tight"
                    style={{ color: YELLOW }}
                  >
                    {vuelo.puerta?.trim() ? vuelo.puerta : "—"}
                  </span>
                  <span className="text-sm text-white/70">
                    {vuelo.terminal
                      ? `Terminal ${vuelo.terminal}`
                      : vuelo.puerta?.trim()
                        ? ""
                        : "Por asignar"}
                  </span>
                </div>
              </div>

              {eyebrow("Paso 3 — Cómo llegar")}
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight mb-6">
                Ruta a la puerta {vuelo.puerta?.trim() || ""}
              </h2>

              {vuelo.puerta?.trim() ? (
                <GateMap gate={vuelo.puerta} />
              ) : (
                <div
                  className="w-full rounded-2xl border-2 border-dashed border-gray-300 flex items-center justify-center text-center p-8 text-gray-500"
                  style={{ height: 300 }}
                >
                  La puerta aún no está asignada. Vuelve a consultar más cerca de
                  la hora de salida.
                </div>
              )}

              <button
                onClick={restart}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wider font-semibold border transition-colors hover:bg-gray-50"
                style={{ borderColor: `${NAVY}33`, color: NAVY }}
              >
                <RotateCcw className="w-4 h-4" />
                Escanear otro
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
